import { useMemo } from 'react'
import {
  queryOptions,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
  useQueries,
} from '@tanstack/react-query'
import { COMMENT_API_PREFIX } from 'shared/api/constants/api-constants'
import { PAGINATION_ORDER } from 'shared/constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import type { OnErrorType } from 'modules/comments/types'
import { commentSourceKeyMap } from 'modules/comments/utils/constants'
import { mergeComments } from 'modules/comments/utils/merge-comments'
import { commentsKeys, type PinnedCommentsParams } from './key-factory'

export const getPinnedCommentsUrl = ({
  lastCommentId,
  parentId,
  postId,
  sortOrder,
  targetCommentId,
}: PinnedCommentsParams): string => {
  const searchParams = new URLSearchParams()

  searchParams.set('pagination[order]', sortOrder)
  searchParams.set(commentSourceKeyMap.community, postId.toString())
  searchParams.set('parent', parentId?.toString() ?? 'null')
  if (targetCommentId) searchParams.set('commentId', targetCommentId.toString())
  if (lastCommentId !== 0) searchParams.set('pagination[startFrom]', lastCommentId.toString())

  return `${COMMENT_API_PREFIX}/pinned?${searchParams.toString()}`
}

interface UsePinnedCommentsOptions {
  postId: number
  targetCommentId?: number
  initialData?: CommentInterface[]
  enabled?: boolean
  onError?: OnErrorType
}

export const usePinnedComments = ({
  initialData,
  enabled = true,
  onError,
  postId,
  targetCommentId,
}: UsePinnedCommentsOptions): Pick<
  UseInfiniteQueryResult<CommentInterface[]>,
  'data' | 'fetchNextPage' | 'isError' | 'isFetching' | 'isLoading' | 'refetch'
> => {
  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.get, CommentInterface[]>({
    method: RequestMethodsEnum.get,
  })
  const queryMeta = { onError, sentryContext: 'fetch pinned comments error' }
  const baseQuery = useInfiniteQuery<
    CommentInterface[],
    Error,
    CommentInterface[],
    ReturnType<typeof commentsKeys.pinned>,
    number
  >({
    queryKey: commentsKeys.pinned(postId),
    queryFn: async ({ pageParam, signal }) => {
      const data = await fetcher(
        getPinnedCommentsUrl({
          lastCommentId: pageParam,
          parentId: null,
          postId,
          sortOrder: PAGINATION_ORDER.ASC,
        }),
      )
      signal.throwIfAborted()
      return data
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage, pages) => {
      let comments: CommentInterface[] = []
      for (const page of pages) comments = mergeComments(comments, page)
      const commentWithMissingNext = comments.find(
        (comment, index) => comment.nextId !== null && comments[index + 1]?.id !== comment.nextId,
      )
      return commentWithMissingNext?.id
    },
    // Defined initial data lets invalidation replace an in-flight first read instead of joining it.
    initialData: { pages: [initialData ?? []], pageParams: [0] },
    select: data => {
      let comments: CommentInterface[] = []
      for (const page of data.pages) comments = mergeComments(comments, page)
      return comments
    },
    // A zero timestamp keeps the seeded page stale until the server response arrives.
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
    meta: queryMeta,
  })
  const baseComments = baseQuery.data
  const focusTargetCommentIds =
    targetCommentId !== undefined &&
    !baseComments.some(
      comment =>
        comment.id === targetCommentId ||
        comment.children?.some(child => child.id === targetCommentId) === true,
    )
      ? [targetCommentId]
      : []
  const {
    comments: focusComments,
    isFetching: isFetchingFocus,
    retryFailedQuery,
  } = useQueries({
    queries: focusTargetCommentIds.map(focusedCommentId =>
      queryOptions({
        queryKey: commentsKeys.pinnedFocus(postId, focusedCommentId),
        queryFn: async ({ signal }) => {
          const data = await fetcher(
            getPinnedCommentsUrl({
              lastCommentId: 0,
              parentId: null,
              postId,
              sortOrder: PAGINATION_ORDER.ASC,
              targetCommentId: focusedCommentId,
            }),
          )
          signal.throwIfAborted()
          return data
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled,
        meta: queryMeta,
      }),
    ),
    combine: queries => {
      let comments: CommentInterface[] = []
      const failedQuery = queries.find(query => query.error)
      for (const query of queries) {
        if (query.data) comments = mergeComments(comments, query.data)
      }
      const retryFailedFocusQuery =
        failedQuery === undefined ? undefined : async (): Promise<unknown> => failedQuery.refetch()
      return {
        comments,
        isFetching: queries.some(query => query.isFetching),
        retryFailedQuery: retryFailedFocusQuery,
      }
    },
  })
  const data = useMemo(
    () => mergeComments(baseComments, focusComments),
    [baseComments, focusComments],
  )
  const isFetching = baseQuery.isFetching || isFetchingFocus
  const refetch: typeof baseQuery.refetch = async options => {
    if (retryFailedQuery) await retryFailedQuery()
    return baseQuery.refetch(options)
  }

  return {
    data,
    fetchNextPage: baseQuery.fetchNextPage,
    isError: baseQuery.isError || retryFailedQuery !== undefined,
    isFetching,
    isLoading: baseQuery.isFetching && baseQuery.dataUpdatedAt === 0,
    refetch,
  }
}
